import TeamListPagination from '@features/organisation/components/teamList/TeamListPagination'
import { type Device, DeviceStatus, type Home } from '@lmc/ts-common'
import { Dropdown } from '@lmc/ui-kit'
import { Button, Text } from '@lmc/ui-kit/src/general'
import ResidentCell from '@lmc/ui-kit/src/legacy/molecules/Table/cells/ResidentCell'
import Table from '@lmc/ui-kit/src/legacy/molecules/Table/Table'
import { Menu, type TablePaginationConfig } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { showToast } from 'common/utils'
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog'
import dayjs from 'dayjs'
import useUsersList from 'hooks/useUser/useUsersList'
import { NAMESPACE } from 'locale'
import { dacConstants } from 'locale/deviceAccessControl'
import type { FC, ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import DotsButton from 'screens/Residents/screens/ResidentsGoalsScreen/components/DotsMenu/DotsMenu'
import styled from 'styled-components'
import { useUpdateDeviceById } from '../../hooks'
import { DEVICES_PER_PAGE, type TableParams } from '../../types'
import { cssDeviceTable } from './deviceTableStyles'

type DevicesTableProps = {
  devices: Device[]
  tableParams: TableParams
  handleTableChange: (pagination: TablePaginationConfig) => void
  hasDeviceAccessControl?: boolean
  navigate: (route: string) => void
  refetch: () => void
  testId?: string
}

const DevicesTable: FC<DevicesTableProps> = ({
  devices,
  tableParams,
  handleTableChange,
  hasDeviceAccessControl,
  navigate,
  refetch,
  testId
}) => {
  const { t } = useTranslation(NAMESPACE.DEVICE_ACCESS_CONTROL)

  const home: string =
    useSelector((state: { global: { home: Home } }) => state.global.home.id) ||
    ''

  const { updateDevice, isLoading: isLoadingUpdateDevice } =
    useUpdateDeviceById(() => refetch())

  const rows = devices?.map((device, index) => ({
    deviceName: device.name,
    deviceModel: device.model,
    lastSeen: dayjs(device.lastUsedOn).format('HH:mm DD/MM/YYYY '),
    lastUsedBy: device.lastUsedBy.id,
    action: {
      id: device.id,
      index: index,
      name: device.name,
      status: (device as unknown as { status: DeviceStatus })?.status
    },
    key: device.id,
    appVersion: device?.version || 'N/A'
  }))

  const { users } = useUsersList({})

  const handleChangeStatus = (status: DeviceStatus, home: string, id: string) =>
    updateDevice({
      id,
      homes: [{ status, home }]
    })
      .then(() =>
        showToast({
          type: 'success',
          title: t(
            status === DeviceStatus.approved
              ? dacConstants.DEVICE_APPROVED
              : dacConstants.DEVICE_BLOCKED
          )
        })
      )
      .catch(() =>
        showToast({ type: 'error', title: t(dacConstants.UPDATE_FAILED) })
      )

  const withApproveConfirmation = (children: ReactElement) => (
    <ConfirmationDialog
      title={t(dacConstants.APPROVING_DEVICE)}
      confirmText={t(dacConstants.APPROVE_DEVICE_ACTION)}
      confirmVariant="primary"
      description={
        <>
          <Text level={1}>{t(dacConstants.APPROVE_DEVICE_CONFIRM_DESC)}</Text>
          <StyledText level={1}>
            {t(dacConstants.ARE_YOU_SURE_CONTINUE)}
          </StyledText>
        </>
      }
    >
      {children}
    </ConfirmationDialog>
  )

  const withBlockConfirmation = (children: ReactElement) => (
    <ConfirmationDialog
      title={t(dacConstants.BLOCKING_DEVICE)}
      confirmText={t(dacConstants.BLOCK_DEVICE_ACTION)}
      confirmVariant="danger"
      description={
        <>
          <Text level={1}>{t(dacConstants.BLOCK_DEVICE_CONFIRM_DESC)}</Text>
          <StyledText level={1}>
            {t(dacConstants.ARE_YOU_SURE_CONTINUE)}
          </StyledText>
        </>
      }
    >
      {children}
    </ConfirmationDialog>
  )

  const pageSize = tableParams?.pagination?.pageSize ?? DEVICES_PER_PAGE
  const totalItems = tableParams?.pagination?.total ?? 0
  const currentPage = tableParams?.pagination?.current ?? 1
  const totalPages = Math.ceil(totalItems / pageSize)

  const handlePageChange = (page: number) =>
    handleTableChange({ ...tableParams.pagination, current: page })

  const columns: ColumnsType<(typeof rows)[0]> = [
    {
      dataIndex: 'lastSeen',
      width: '17%',
      title: t(
        hasDeviceAccessControl
          ? dacConstants.SIGN_IN_TIME
          : dacConstants.LAST_SEEN_TIME
      )
    },
    {
      dataIndex: 'deviceModel',
      width: '15%',
      title: t(dacConstants.DEVICE_MODEL)
    },
    {
      dataIndex: 'appVersion',
      width: '11%',
      title: t(dacConstants.APP_VERSION)
    },
    {
      dataIndex: 'deviceName',
      width: '16%',
      title: t(dacConstants.DEVICE_NAME)
    },
    {
      dataIndex: 'lastUsedBy',
      width: '24%',
      title: t(dacConstants.LAST_USED_BY),
      render: (id: string) => {
        const user = users.find((u) => u.id === id)
        return (
          user && (
            <ResidentCell
              avatarSize={30}
              resident={{
                name: user.name,
                picture: user?.picture as unknown as string
              }}
            />
          )
        )
      }
    },

    {
      dataIndex: 'action',
      width: '17%',
      title: t(dacConstants.ACTIONS),
      render: ({ id, index, status }) => {
        if (!hasDeviceAccessControl) {
          return (
            <div
              onClick={(e) => e.stopPropagation()}
              data-testid={testId ? `${testId}-actions-${index}` : undefined}
            >
              <Button
                variant="default"
                onClick={() => navigate(`/organisation/devices/${id}/manage`)}
                data-cy="btn-manage"
                data-testid={testId ? `${testId}-manage-${index}` : undefined}
              >
                {t(dacConstants.MANAGE)}
              </Button>
            </div>
          )
        }

        const isApproved = status === DeviceStatus.approved
        const isPending = status === DeviceStatus.pending

        const primaryAction = isApproved
          ? withBlockConfirmation(
              <Button
                variant="default"
                onClick={() =>
                  handleChangeStatus(DeviceStatus.blocked, home, id)
                }
                data-cy="btn-block"
                data-testid={testId ? `${testId}-block-${index}` : undefined}
              >
                {t(dacConstants.BLOCK)}
              </Button>
            )
          : withApproveConfirmation(
              <Button
                variant="default"
                onClick={() =>
                  handleChangeStatus(DeviceStatus.approved, home, id)
                }
                data-cy="btn-approve"
                data-testid={testId ? `${testId}-approve-${index}` : undefined}
              >
                {t(dacConstants.APPROVE)}
              </Button>
            )

        return (
          <ActionsCellWrapper
            onClick={(e) => e.stopPropagation()}
            data-testid={testId ? `${testId}-actions-${index}` : undefined}
          >
            {primaryAction}
            <Dropdown
              overlay={
                <DropdownMenu
                  data-testid={
                    testId ? `${testId}-actions-menu-${index}` : undefined
                  }
                >
                  {isPending &&
                    withBlockConfirmation(
                      <Menu.Item
                        data-cy="btn-block-menu"
                        key="block"
                        onClick={() =>
                          handleChangeStatus(DeviceStatus.blocked, home, id)
                        }
                        data-testid={
                          testId ? `${testId}-block-menu-${index}` : undefined
                        }
                      >
                        {t(dacConstants.BLOCK_DEVICE_ACTION)}
                      </Menu.Item>
                    )}
                  <Menu.Item
                    data-cy="btn-manage"
                    key="manage"
                    onClick={() =>
                      navigate(`/organisation/devices/${id}/manage`)
                    }
                    data-testid={
                      testId ? `${testId}-manage-${index}` : undefined
                    }
                  >
                    {t(dacConstants.MANAGE_DEVICE)}
                  </Menu.Item>
                  <Menu.Item
                    data-cy="btn-view-device-sign"
                    key="view"
                    onClick={() =>
                      navigate(`/organisation/devices/signs/?deviceId=${id}`)
                    }
                    data-testid={
                      testId ? `${testId}-view-device-${index}` : undefined
                    }
                  >
                    {t(dacConstants.VIEW_DEVICE)}
                  </Menu.Item>
                </DropdownMenu>
              }
              trigger={['click']}
              placement="bottomRight"
            >
              <DotsButton
                horizontal={false}
                data-testid={
                  testId ? `${testId}-dots-button-${index}` : undefined
                }
              />
            </Dropdown>
          </ActionsCellWrapper>
        )
      }
    }
  ]

  return (
    <>
      <TableStyled<(typeof rows)[0]>
        columns={columns}
        data-testid={testId}
        dataSource={rows}
        loading={isLoadingUpdateDevice}
        pagination={false}
        tableLayout="fixed"
      />
      <TeamListPagination
        currentPage={currentPage}
        onPageChange={handlePageChange}
        testId={testId ? `${testId}-pagination` : undefined}
        totalPages={totalPages}
      />
    </>
  )
}

const StyledText = styled(Text)`
  font-weight: 500;
`

const ActionsCellWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

// The dropdown renders in a portal, so its items can't be reached via the
// table styles — bump the menu-item text to 16px to match the table body.
const DropdownMenu = styled(Menu)`
  && {
    border-radius: 8px;
  }
  && .ant-dropdown-menu-item {
    font-size: 16px;
  }
`

const TableStyled = styled(Table)`
  ${cssDeviceTable}
` as typeof Table

export default DevicesTable
